<template>
  <div :id="component_id" class="w-full px-6 pt-2 pb-6 inline-flex flex-col justify-start items-start gap-6 overflow-hidden">
    <div class="self-stretch flex flex-col justify-start items-start">
      <div class="self-stretch flex flex-col justify-start items-start gap-8">
        <div class="self-stretch flex flex-col justify-start items-start gap-8">
          <div class="self-stretch flex flex-col justify-start items-start gap-2">
            <div class="justify-start text-text-default/80 text-base font-semibold leading-5">{{ $t('settings.rey.personalization.title') }}</div>
            <div class="self-stretch justify-start text-text-subtle text-xs leading-4 tracking-tight">{{ $t('settings.rey.personalization.description') }}</div>
          </div>
          <div class="self-stretch inline-flex justify-start items-start gap-4">
            <div class="flex-1 rounded-tl rounded-tr inline-flex flex-col justify-start items-start">
              <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
                <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.rey.displayName.label') }}</div>
              </div>
              <div class="self-stretch px-3 bg-surface-1 rounded outline outline-1 outline-offset-[-1px] outline-border-default flex flex-col justify-start items-start gap-2.5 overflow-hidden">
                <div class="self-stretch inline-flex justify-start items-center gap-3 overflow-hidden">
                  <div class="flex-1 h-10 flex justify-start items-center">
                    <input v-model="display_name" type="text" class="w-full text-text-default/80 text-base leading-5 bg-surface-1 border-none outline-none" :placeholder="$t('settings.rey.displayName.placeholder')" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="self-stretch rounded-tl rounded-tr flex flex-col justify-start items-start">
            <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
              <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.rey.bio.label') }}</div>
            </div>
            <div class="self-stretch h-36 px-3 py-2 bg-surface-1 rounded outline outline-1 outline-offset-[-1px] outline-border-default flex flex-col justify-start items-start overflow-hidden">
              <textarea v-model="bio" class="w-full h-full text-text-default/80 text-base leading-5 bg-surface-1 border-none outline-none resize-none" :placeholder="$t('settings.rey.bio.placeholder')" maxlength="500"></textarea>
            </div>
            <div class="self-stretch text-right justify-start text-text-subtle text-xs leading-4 tracking-tight">{{ (bio || '').length }} / 500</div>
          </div>
          <div class="self-stretch flex flex-col justify-start items-start">
            <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
              <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.rey.spokenLanguage.label') }}</div>
            </div>
            <fusion-dropdown :key="`${component_id}_spoken_language`" v-model="spoken_language" :options="language_options" :placeholder="$t('common.selectLanguage')" />
            <div class="self-stretch justify-start text-text-subtle text-xs leading-4 tracking-tight mt-1">{{ $t('settings.rey.spokenLanguage.description') }}</div>
          </div>
          <div class="self-stretch flex flex-col justify-start items-start">
            <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
              <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.rey.personality.label') }}</div>
            </div>
            <fusion-dropdown :key="`${component_id}_personality`" v-model="personality" :options="personality_options" :placeholder="$t('settings.rey.personality.placeholder')" />
            <div class="self-stretch justify-start text-text-subtle text-xs leading-4 tracking-tight mt-1">{{ $t('settings.rey.personality.description') }}</div>
          </div>
          <div class="self-stretch flex flex-col justify-start items-start">
            <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
              <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.rey.voice.label') }}</div>
            </div>
            <fusion-dropdown :key="`${component_id}_voice`" v-model="voice" :options="voice_options" :placeholder="$t('settings.rey.voice.placeholder')" />
            <div class="self-stretch justify-start text-text-subtle text-xs leading-4 tracking-tight mt-1">{{ $t('settings.rey.voice.description') }}</div>
          </div>
          <div class="self-stretch flex flex-col justify-start items-start">
            <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
              <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.rey.dateFormat.label') }}</div>
            </div>
            <!-- sort-order="none": FusionDropdown alphabetizes by label by default, which would
                 reorder these to 08/26/2026, 2026-08-26, 26 August 2026, ... and bury the "Match
                 my language" default at the bottom. These options have a meaningful natural order. -->
            <fusion-dropdown :key="`${component_id}_date_format`" v-model="date_format" :options="date_format_options" sort-order="none" :placeholder="$t('settings.rey.dateFormat.placeholder')" />
            <div class="self-stretch justify-start text-text-subtle text-xs leading-4 tracking-tight mt-1">{{ $t('settings.rey.dateFormat.description') }}</div>
          </div>
          <div class="self-stretch rounded-tl rounded-tr flex flex-col justify-start items-start">
            <div class="self-stretch pb-1 bg-white/0 inline-flex justify-start items-center">
              <div class="flex-1 justify-start text-text-subtle text-xs font-medium leading-4 tracking-tight">{{ $t('settings.rey.customInstructions.label') }}</div>
            </div>
            <div class="self-stretch h-36 px-3 py-2 bg-surface-1 rounded outline outline-1 outline-offset-[-1px] outline-border-default flex flex-col justify-start items-start overflow-hidden">
              <textarea v-model="custom_instructions" class="w-full h-full text-text-default/80 text-base leading-5 bg-surface-1 border-none outline-none resize-none" :placeholder="$t('settings.rey.customInstructions.placeholder')" maxlength="500"></textarea>
            </div>
            <div class="self-stretch text-right justify-start text-text-subtle text-xs leading-4 tracking-tight">{{ (custom_instructions || '').length }} / 500</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FusionDropdown from '@components/UI/FusionDropdown.vue';
import { getEnabledLocales } from '../../i18n';

export default {
  components: {
    FusionDropdown
  },
  props: {
    settings: {
      type: Object,
      required: false,
      default: null
    },
  },
  data() {
    return {
      // Wired fields - using correct backend key names as variable names for clarity
      display_name: '',
      bio: null,
      spoken_language: 'en-US',
      personality: 'concise',
      voice: 'neutral-female-1',
      // Must match DateFormatRegistry.DefaultKey and UxSettings.DateFormat. If these three
      // disagree, this screen misreports what Rey is actually doing and the dropdown becomes a
      // no-op for anyone who never set it (settings_patch only emits changed keys).
      date_format: 'auto',
      custom_instructions: null,
      
      // Options using backend format values
      // personality_options / voice_options are computed so their labels follow the UI language.
      //
      // Language names are deliberately NOT translated: each is shown in its own language
      // (endonym) so a user can find their language whatever the current interface language is.
      // language_options moved to computed (below): it is gated by the instance's enabled locales
      // (server-supported reply languages — German is opt-in per instance), same as GeneralSettings.

      // Track original values for change detection
      original_values: {},
    }
  },
  created() {
    this.loadFromSettings();
  },
  computed: {
    component_key() {
      return this.$attrs.key ? this.$attrs.key : ('rey_preferences_' + Date.now());
    },
    component_id() {
      return this.component_key + '_container';
    },
    // Localized dropdown options. Values stay in the backend's format — only labels are localized.
    // Language names are endonyms (see the data() note above); the list is filtered by the
    // instance's enabled locales, compared by primary tag since the full backend values carry
    // regional subtags (de-DE vs de).
    language_options() {
      const enabled = getEnabledLocales();
      return [
        { label: 'English (US)', value: 'en-US' },
        { label: 'English (UK)', value: 'en-GB' },
        { label: 'Español (México)', value: 'es-MX' },
        { label: 'Français (Canada)', value: 'fr-CA' },
        { label: 'Français (France)', value: 'fr-FR' },
        { label: 'Deutsch', value: 'de-DE' },
      ].filter(option => enabled.includes(option.value.split('-')[0].toLowerCase()));
    },
    personality_options() {
      return [
        { label: this.$t('settings.rey.personalities.concise'), value: 'concise' },
        { label: this.$t('settings.rey.personalities.enthusiastic'), value: 'enthusiastic' },
        { label: this.$t('settings.rey.personalities.friendly'), value: 'friendly' },
        { label: this.$t('settings.rey.personalities.professional'), value: 'professional' },
      ];
    },
    // The full voice catalog, including voices not currently offered. `available` gates
    // visibility: a voice is pickable only once its profile is registered on the TTS gateway
    // (a reference .wav on the inference gateway's TTS service, the voice name being the filename stem).
    // Only the neutral female voice is offered today, and it is served by the `smiling` profile
    // -- the one voice the gateway actually has.
    //
    // Unavailable entries stay here rather than being deleted so a voice can be unblocked by
    // flipping its flag once the gateway has the profile. They also keep their $t() call, which
    // is load-bearing: i18n/index.spec.js scans component source for $t('...') literals and
    // fails any catalog key no component references, so the labels for the hidden voices would
    // be reported as unused the moment their $t() call disappeared.
    all_voice_options() {
      return [
        { label: this.$t('settings.rey.voices.neutralFemale'), value: 'neutral-female-1', available: true },
        { label: this.$t('settings.rey.voices.neutralMale'), value: 'neutral-male-1', available: false },
        { label: this.$t('settings.rey.voices.warmFemale'), value: 'warm-female-1', available: false },
        { label: this.$t('settings.rey.voices.warmMale'), value: 'warm-male-1', available: false },
        { label: this.$t('settings.rey.voices.energeticFemale'), value: 'energetic-female-1', available: false },
        { label: this.$t('settings.rey.voices.energeticMale'), value: 'energetic-male-1', available: false },
      ];
    },
    // Values are DateFormatRegistry keys and must stay in that order — this list is NOT sorted
    // alphabetically because the options have a meaningful natural order: "auto" first as the
    // default, then spelled-out forms, then numeric, then ISO. Only the "auto" label is
    // translated; the other five ARE the format, shown as a literal example date, so translating
    // them would misrepresent what the user is picking.
    // The two MONTH-FIRST options carry a hint: month-before-day is not a convention in Spanish or
    // French, so Rey honours those shapes only in an English answer and writes the language's own
    // form otherwise (see DateFormatDefinition.NonEnglishFormatKey). The hint is a separate field,
    // not part of the label, so the labels stay the literal untranslated example dates.
    date_format_options() {
      const english_only = this.$t('settings.rey.dateFormats.englishOnly');
      return [
        { label: this.$t('settings.rey.dateFormats.auto'), value: 'auto' },
        { label: 'August 26, 2026', value: 'long_month_first', hint: english_only },
        { label: '26 August 2026', value: 'long_day_first' },
        { label: '08/26/2026', value: 'numeric_month_first', hint: english_only },
        { label: '26/08/2026', value: 'numeric_day_first' },
        { label: '2026-08-26', value: 'iso' },
      ];
    },
    // What the dropdown renders, and what resolveVoice() validates against -- a hidden voice is
    // absent from both, so a stored id for one coerces to the default like any unknown voice.
    voice_options() {
      return this.all_voice_options.filter(option => option.available);
    },
    // Build patch object with only changed values
    settings_patch() {
      const patch = {};
      
      // Check each wired field for changes
      if (this.display_name !== this.original_values.display_name) {
        patch['Profile.DisplayName'] = this.display_name;
      }
      if (this.bio !== this.original_values.bio) {
        patch['Profile.Bio'] = this.bio;
      }
      if (this.spoken_language !== this.original_values.spoken_language) {
        patch['Ux.SpokenLanguage'] = this.spoken_language;
        patch['Ux.UseSystemLanguageAsSpoken'] = false;
      }
      if (this.personality !== this.original_values.personality) {
        patch['Ux.Personality'] = this.personality;
      }
      if (this.voice !== this.original_values.voice) {
        patch['Ux.Voice.Id'] = this.voice;
      }
      if (this.date_format !== this.original_values.date_format) {
        patch['Ux.DateFormat'] = this.date_format;
      }
      if (this.custom_instructions !== this.original_values.custom_instructions) {
        patch['Ux.CustomInstructions'] = this.custom_instructions;
      }
      
      return patch;
    },
    has_changes() {
      return Object.keys(this.settings_patch).length > 0;
    },
  },
  watch: {
    has_changes: {
      handler(newVal) {
        this.$emit('changes', newVal);
      },
      immediate: true
    }
  },
  methods: {
    // Map a stored voice id onto one the picker actually offers, falling back to the default.
    resolveVoice(voiceId) {
      const fallback = 'neutral-female-1';
      if (!voiceId) return fallback;
      return this.voice_options.some(option => option.value === voiceId) ? voiceId : fallback;
    },
    loadFromSettings() {
      const flat = this.settings?.flat || {};
      
      // Load wired fields from backend using correct keys
      this.display_name = flat['Profile.DisplayName'] || '';
      this.bio = flat['Profile.Bio'] || null;
      this.spoken_language = flat['Ux.SpokenLanguage'] || 'en-US';
      this.personality = flat['Ux.Personality'] || 'concise';
      // A saved voice the picker no longer offers is shown as the default rather than left
      // unmatched -- the dropdown renders its placeholder for an unknown value, which would read
      // as "nothing selected" while the server was in fact synthesizing. Coercing here keeps the
      // displayed voice honest; original_values takes the coerced value too, so this does not by
      // itself count as an unsaved change.
      this.voice = this.resolveVoice(flat['Ux.Voice.Id']);
      this.date_format = flat['Ux.DateFormat'] || 'auto';
      this.custom_instructions = flat['Ux.CustomInstructions'] || null;
      
      // Store original values for change detection
      this.original_values = {
        display_name: this.display_name,
        bio: this.bio,
        spoken_language: this.spoken_language,
        personality: this.personality,
        voice: this.voice,
        date_format: this.date_format,
        custom_instructions: this.custom_instructions,
      };
    },
    save() {
      const patch = this.settings_patch;
      if (Object.keys(patch).length > 0) {
        this.$emit('save', patch);
      } else {
        // No changes, just close
        this.$emit('cancel');
      }
    },
    cancel() {
      this.$emit('cancel');
    },
  },
}
</script>
